import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { DownloadMenu } from './DownloadMenu';
import { downloadAsDocx, downloadAsTxt } from '../lib/download';
import { generateStory } from '../lib/utils';
import { generateEnhancedStory } from '../lib/gemini';
import type { FormData } from '../types/form';

interface PreviewProps {
  data: FormData;
  onEdit: () => void;
}

export function Preview({ data, onEdit }: PreviewProps) {
  const [story, setStory] = useState(generateStory(data));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function enhanceStory() {
      try {
        setIsLoading(true);
        setError(null);
        const enhancedStory = await generateEnhancedStory(data);
        setStory(enhancedStory);
      } catch (err) {
        setError('Failed to generate enhanced story. Showing basic version instead.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }

    enhanceStory();
  }, [data]);

  const handleDownloadTxt = () => {
    downloadAsTxt(story);
  };

  const handleDownloadDocx = () => {
    downloadAsDocx(story);
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 md:px-6 space-y-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <button
          onClick={onEdit}
          className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Edit</span>
        </button>

        <div className="flex flex-col md:flex-row gap-4 md:items-center">
          <DownloadMenu
            onDownloadTxt={handleDownloadTxt}
            onDownloadDocx={handleDownloadDocx}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#60a3bc]"></div>
          </div>
        ) : (
          <div className="prose dark:prose-invert max-w-none">
            {error && (
              <div className="text-red-500 mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {error}
              </div>
            )}
            <div className="whitespace-pre-wrap font-sans text-gray-800 dark:text-gray-200 text-lg leading-relaxed break-words">
              {story}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}